import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ACCESS_ACTIONS, type EmailAdmin as EmailAdminData, type MailboxInfo, type PersonalInboxRow } from '@shared/gmail';
import type { TeamMember } from '@shared/types';
import { ApiError, api, errorMessage } from '@/lib/api';
import { daysFromToday, formatAgo, formatDate, formatNumber, formatTime, plural } from '@/lib/format';
import { useMe, useTeam } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { toast, toastError } from '@/lib/toast';
import { Avatar } from '@/ui/Avatar';
import { Callout, Chip, Empty, Skeleton } from '@/ui/bits';
import { copyWithToast } from '@/ui/Copy';
import { Icon } from '@/ui/Icon';
import { GuideBox } from './Guide';
import { CrmBox, SharedBox, disconnectMailbox, disconnectMine } from './Inboxes';
import { ACTION_LABELS, LogRows, inboxLabel } from './log';
import { adminLogPage, connectUrl, emailAdminQuery, firstName, goConnect, type LogFilters } from './queries';

// "Email & inboxes" in the admin console: personal inboxes and the guard around them, the access log, shared
// inboxes, the CRM address and the Google Cloud setup guide.

export function EmailAdmin() {
  const q = useQuery(emailAdminQuery);
  return (
    <div aria-busy={q.isLoading || undefined}>
      <div className="adm-h">
        <div className="grow">
          <h2>Email &amp; inboxes</h2>
          <p>Who can read which inbox, and the log of every access.</p>
        </div>
      </div>
      {q.isLoading ? (
        <div className="agrid">
          <div className="tile span-6">
            <Skeleton rows={5} />
          </div>
          <div className="tile span-6">
            <Skeleton rows={5} />
          </div>
          <div className="tile span-12">
            <Skeleton rows={4} />
          </div>
        </div>
      ) : q.isError || !q.data ? (
        <Callout
          tone="danger"
          role="alert"
          title="Email settings did not load"
          actions={
            <button type="button" className="btn btn-sm btn-tile" onClick={() => q.refetch()} disabled={q.isRefetching}>
              <Icon name="refresh" />
              {q.isRefetching ? 'Trying again' : 'Try again'}
            </button>
          }
        >
          {errorMessage(q.error)}
        </Callout>
      ) : (
        <Sections data={q.data} />
      )}
    </div>
  );
}

function Sections({ data }: { data: EmailAdminData }) {
  const configured = data.google.configured;
  return (
    <div className="agrid">
      {!configured ? <GuideBox data={data} /> : null}
      {configured ? <WaitingCallout data={data} /> : null}
      <ErrorCallouts data={data} />
      <PersonalBox data={data} />
      <GuardBox data={data} />
      <LogBox data={data} />
      <SharedBox data={data} />
      <CrmBox data={data} />
      {configured ? <GuideBox data={data} /> : null}
    </div>
  );
}

/* ---------- reminders ---------- */

function connectLink(data: EmailAdminData): string {
  return `${data.baseUrl.replace(/\/$/, '')}/?connect=gmail`;
}

function useNudge(data: EmailAdminData) {
  const [busy, setBusy] = useState<string | null>(null);
  const send = async (member: TeamMember) => {
    if (busy) return;
    setBusy(member.id);
    try {
      const res = await api.post<{ ok: true; at: string }>(`/gmail/admin/nudge/${member.id}`);
      queryClient.setQueryData<EmailAdminData>(emailAdminQuery.queryKey, (d) =>
        d ? { ...d, personal: d.personal.map((p) => (p.userId === member.id ? { ...p, nudgedAt: res.at } : p)) } : d,
      );
      toast(`Nudge sent to ${firstName(member.name)} with a one-click connect link`, { icon: 'send' });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'no_sending_inbox') {
        toast(err.message, { error: true, action: { label: 'Copy link', run: () => void copyWithToast(connectLink(data), 'Link') } });
      } else {
        toastError(err);
      }
    } finally {
      setBusy(null);
    }
  };
  return { busy, send };
}

function NudgeButton({ data, member, nudge, label }: { data: EmailAdminData; member: TeamMember; nudge: ReturnType<typeof useNudge>; label: string }) {
  if (!data.canSendNudge) {
    return (
      <button type="button" className="btn btn-sm btn-ghost" onClick={() => void copyWithToast(connectLink(data), 'Link')} title={`A link ${firstName(member.name)} can open to connect`}>
        <Icon name="copy" />
        Copy connect link
      </button>
    );
  }
  const busy = nudge.busy === member.id;
  return (
    <button type="button" className="btn btn-sm btn-tile" onClick={() => nudge.send(member)} disabled={!!nudge.busy} aria-busy={busy || undefined}>
      <Icon name="send" />
      {busy ? 'Sending' : label}
    </button>
  );
}

function WaitingCallout({ data }: { data: EmailAdminData }) {
  const me = useMe();
  const { byId } = useTeam();
  const nudge = useNudge(data);
  const waiting = data.personal
    .filter((p) => !p.mailbox && p.userId !== me.user.id)
    .map((p) => byId.get(p.userId))
    .filter((m): m is TeamMember => !!m);
  if (waiting.length === 0) return null;
  if (waiting.length === 1) {
    const m = waiting[0]!;
    const first = firstName(m.name);
    return (
      <Callout
        tone="warn"
        className="span-12"
        title={`${m.name}'s Gmail is waiting to connect`}
        actions={<NudgeButton data={data} member={m} nudge={nudge} label={`Send ${first} a reminder`} />}
      >
        <div className="gm-callout-text">
          Replies to {first}’s CRM emails are tracked once {first} connects Gmail.
          {data.canSendNudge ? null : ' Reminders go out from your own Gmail, so for now copy the link and send it yourself.'}
        </div>
      </Callout>
    );
  }
  return (
    <Callout tone="warn" className="span-12" title={`${plural(waiting.length, 'teammate')} waiting to connect Gmail`}>
      <div className="gm-callout-text">
        Replies to CRM emails from {waiting.map((m) => firstName(m.name)).join(', ')} are tracked once they connect. Send each a
        nudge from Personal inboxes.
      </div>
    </Callout>
  );
}

function ErrorCallouts({ data }: { data: EmailAdminData }) {
  const { byId } = useTeam();
  const me = useMe();
  const broken: { mb: MailboxInfo; who: string; kind: 'personal' | 'shared' | 'crm' }[] = [];
  for (const p of data.personal) {
    if (p.mailbox?.status === 'error') {
      const m = byId.get(p.userId);
      broken.push({ mb: p.mailbox, who: p.userId === me.user.id ? 'Your Gmail' : `${m ? m.name : p.mailbox.address}'s Gmail`, kind: 'personal' });
    }
  }
  for (const s of data.shared) if (s.status === 'error') broken.push({ mb: s, who: s.address, kind: 'shared' });
  if (data.crm?.status === 'error') broken.push({ mb: data.crm, who: 'The CRM address', kind: 'crm' });
  if (broken.length === 0) return null;
  return (
    <>
      {broken.map(({ mb, who, kind }) => {
        const mine = kind === 'personal' && mb.userId === me.user.id;
        return (
          <Callout
            key={mb.id}
            tone="danger"
            className="span-12"
            role="alert"
            title={`${who} needs reconnecting`}
            actions={
              data.google.configured && (kind !== 'personal' || mine) ? (
                <button type="button" className="btn btn-sm btn-tile" onClick={() => goConnect(kind, kind === 'personal' ? undefined : mb.address)}>
                  <Icon name="refresh" />
                  Reconnect
                </button>
              ) : null
            }
          >
            <div className="gm-callout-text">
              {mb.statusDetail ?? 'Google stopped accepting Clientbook’s access.'}{' '}
              {kind === 'personal' && !mine ? 'Only the owner can reconnect a personal inbox.' : null}
            </div>
          </Callout>
        );
      })}
    </>
  );
}

/* ---------- personal inboxes ---------- */

function whenShort(at: string): string {
  const d = daysFromToday(at);
  if (d === 0) return `today ${formatTime(at)}`;
  if (d === -1) return `yesterday ${formatTime(at)}`;
  return formatDate(at);
}

function personalLine(row: PersonalInboxRow, push: boolean): string {
  const mb = row.mailbox;
  if (!mb) {
    return row.nudgedAt ? `Waiting to connect · nudged ${whenShort(row.nudgedAt)}` : 'Waiting to connect';
  }
  if (mb.status === 'disconnected') {
    if (!mb.disconnectedAt) return 'Disconnected · reconnect any time';
    const justNow = Date.now() - new Date(mb.disconnectedAt).getTime() < 60_000;
    return `Disconnected ${justNow ? 'just now' : whenShort(mb.disconnectedAt)} · reconnect any time`;
  }
  if (!mb.live) return `${mb.address} · sample inbox`;
  if (mb.status === 'error') return `${mb.address} · ${mb.statusDetail ?? 'Google stopped accepting Clientbook’s access'}`;
  if (push && mb.watchRenewedAt) return `${mb.address} · push renewed ${whenShort(mb.watchRenewedAt)}`;
  if (mb.lastSyncAt) return `${mb.address} · checked ${formatAgo(mb.lastSyncAt).toLowerCase()}`;
  return `${mb.address} · first check under way`;
}

function PersonalBox({ data }: { data: EmailAdminData }) {
  const me = useMe();
  const { byId, isLoading } = useTeam();
  const nudge = useNudge(data);
  const configured = data.google.configured;
  // You first, then everyone with an inbox, then whoever is still waiting; names stay alphabetical within each.
  const rank = (p: (typeof data.personal)[number]) => (p.userId === me.user.id ? 0 : p.mailbox && p.mailbox.status !== 'disconnected' ? 1 : 2);
  const rows = data.personal.filter((p) => byId.has(p.userId)).sort((a, b) => rank(a) - rank(b));
  const connected = data.personal.filter((p) => p.mailbox && p.mailbox.status !== 'disconnected').length;
  const former = data.formerPersonal.filter((mb) => mb.status !== 'disconnected' || mb.keeping);

  return (
    <div className="tile span-6">
      <div className="tile-h">
        <h3>Personal inboxes</h3>
        <span className="cnt">
          {formatNumber(connected)} of {formatNumber(data.personal.length)} connected
        </span>
      </div>
      {isLoading ? (
        <Skeleton rows={4} />
      ) : rows.length === 0 ? (
        <Empty compact icon="users" title="No personal inboxes yet">
          Each active member connects their own Gmail from their account settings.
        </Empty>
      ) : (
        rows.map((row) => {
          const m = byId.get(row.userId)!;
          const mine = row.userId === me.user.id;
          const mb = row.mailbox;
          let control = null;
          if (!mb) {
            control = mine ? (
              configured ? (
                <a className="btn btn-sm btn-tile" href={connectUrl('personal')}>
                  <Icon name="link" />
                  Connect yours
                </a>
              ) : null
            ) : configured ? (
              <NudgeButton data={data} member={m} nudge={nudge} label={row.nudgedAt ? 'Nudge again' : 'Send a nudge'} />
            ) : (
              <Chip tone="plain">Waiting</Chip>
            );
          } else if (mb.status === 'disconnected') {
            control =
              mine && configured ? (
                <a className="btn btn-sm btn-tile" href={connectUrl('personal')}>
                  <Icon name="refresh" />
                  Reconnect
                </a>
              ) : (
                <Chip tone="plain">Disconnected</Chip>
              );
          } else if (mb.status === 'error') {
            control =
              mine && configured ? (
                <a className="btn btn-sm btn-tile" href={connectUrl('personal')}>
                  <Icon name="refresh" />
                  Reconnect
                </a>
              ) : (
                <Chip tone="danger" icon="x-circle">
                  Needs reconnecting
                </Chip>
              );
          } else if (!mb.live) {
            control = <Chip tone="plain" title="A sample inbox: its mail is sample data">Sample</Chip>;
          } else {
            control = (
              <Chip tone="success" icon="check">
                Connected
              </Chip>
            );
          }
          return (
            <div key={row.userId} className={mb && mb.status !== 'disconnected' ? 'inbox-row' : 'inbox-row gm-off'}>
              <Avatar name={m.name} hue={m.hue} size="md" />
              <div className="grow">
                <b>
                  {m.name}
                  {mine ? <span className="muted gm-you"> (you)</span> : null}
                </b>
                <span>{personalLine(row, data.pubsub.active)}</span>
              </div>
              {control}
            </div>
          );
        })
      )}
      {former.length ? <FormerRows list={former} byId={byId} /> : null}
    </div>
  );
}

/** Inboxes of people who have left that Clientbook still has Google access to. */
function FormerRows({ list, byId }: { list: MailboxInfo[]; byId: Map<string, TeamMember> }) {
  return (
    <>
      <div className="tile-h gm-box-h2">
        <h3>People who have left</h3>
      </div>
      {list.map((mb) => {
        const m = mb.userId ? byId.get(mb.userId) : undefined;
        return (
          <div key={mb.id} className="inbox-row">
            {m ? <Avatar name={m.name} hue={m.hue} size="md" /> : null}
            <div className="grow">
              <b>{m ? m.name : mb.address}</b>
              <span>
                {mb.address} · {mb.keeping ? 'copying the attachments of filed emails to file storage, then Google access ends' : 'still connected after leaving'}
              </span>
            </div>
            {mb.keeping ? null : (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => disconnectMailbox(mb, `${m ? firstName(m.name) : 'this'}’s inbox`)}>
                <Icon name="logout" />
                Disconnect
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}

/* ---------- inbox guard ---------- */

function ReplyWindow({ days: savedDays }: { days: number }) {
  const [days, setDays] = useState(savedDays);
  const saved = useRef(savedDays);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(savedDays);

  // Follow changes made elsewhere (another tab, another admin) unless a change of ours is still waiting.
  useEffect(() => {
    if (timer.current) return;
    saved.current = savedDays;
    latest.current = savedDays;
    setDays(savedDays);
  }, [savedDays]);

  const save = async (value: number) => {
    timer.current = null;
    if (value === saved.current) return;
    const previous = saved.current;
    saved.current = value;
    queryClient.setQueryData<EmailAdminData>(emailAdminQuery.queryKey, (d) => (d ? { ...d, replyWindowDays: value } : d));
    try {
      await api.put('/gmail/admin/reply-window', { days: value });
      toast(`Reply window set to ${value} days`, { icon: 'clock' });
    } catch (err) {
      saved.current = previous;
      latest.current = previous;
      setDays(previous);
      queryClient.setQueryData<EmailAdminData>(emailAdminQuery.queryKey, (d) => (d ? { ...d, replyWindowDays: previous } : d));
      toastError(err);
    }
  };

  // Save straight away when the section closes mid-change.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        void save(latest.current);
      }
    },
    [],
  );

  const change = (value: number) => {
    setDays(value);
    latest.current = value;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void save(value), 600);
  };

  return (
    <div className="gm-window">
      <label className="flabel" htmlFor="gm-reply-window">
        Open replies to CRM conversations from the last
      </label>
      <div className="range">
        <input
          id="gm-reply-window"
          type="range"
          min={7}
          max={90}
          step={1}
          value={days}
          aria-label="Reply window in days"
          aria-valuetext={`${days} days`}
          onChange={(e) => change(Number(e.target.value))}
        />
        <span className="rv" aria-hidden="true">
          {days} days
        </span>
      </div>
    </div>
  );
}

function GuardBox({ data }: { data: EmailAdminData }) {
  const you = data.you;
  const yoursOn = !!you && you.status !== 'disconnected';
  const configured = data.google.configured;
  return (
    <div className="tile span-6">
      <div className="tile-h">
        <h3>Inbox guard</h3>
        {configured ? (
          <Chip tone="success" icon="shield">
            Active
          </Chip>
        ) : (
          <Chip tone="plain" icon="shield">
            Starts with Google
          </Chip>
        )}
      </div>
      {data.pubsub.active && (data.publicHttps || data.pubsub.lastPushAt) ? (
        <div className="secret-row">
          <Icon name="zap" />
          <span className="grow">Google announces new mail by push (Gmail watch, renewed daily)</span>
        </div>
      ) : (
        <div className="secret-row gm-wait">
          <Icon name="clock" />
          <span className="grow">
            {data.pubsub.active
              ? 'New mail is checked every few minutes until Google can reach this server by push'
              : 'New mail is checked every few minutes until Gmail push is set up'}
          </span>
        </div>
      )}
      <div className="secret-row">
        <Icon name="users" />
        <span className="grow">Emails between team members are skipped</span>
      </div>
      <div className="secret-row">
        <Icon name="eye" />
        <span className="grow">Every access is logged below</span>
      </div>
      <div className="secret-row">
        <Icon name="logout" />
        <span className="grow">Anyone can disconnect their inbox at any time</span>
        {yoursOn ? (
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => void disconnectMine(you, configured)}>
            Disconnect mine
          </button>
        ) : configured ? (
          <a className="btn btn-sm btn-ghost" href={connectUrl('personal')}>
            <Icon name="refresh" />
            {you ? 'Reconnect mine' : 'Connect mine'}
          </a>
        ) : null}
      </div>
      <ReplyWindow days={data.replyWindowDays} />
    </div>
  );
}

/* ---------- access log ---------- */

function filtersFrom(inbox: string, action: string): LogFilters {
  const f: LogFilters = {};
  if (inbox.startsWith('kind:')) f.kind = inbox.slice(5) as LogFilters['kind'];
  else if (inbox.startsWith('mb:')) f.mailboxId = inbox.slice(3);
  if (action) f.action = action;
  return f;
}

function LogBox({ data }: { data: EmailAdminData }) {
  const { byId } = useTeam();
  const [inbox, setInbox] = useState('');
  const [action, setAction] = useState('');
  const filters = useMemo(() => filtersFrom(inbox, action), [inbox, action]);
  const filtered = !!inbox || !!action;

  const log = useInfiniteQuery({
    queryKey: ['admin', 'email', 'log', filters],
    queryFn: ({ pageParam, signal }) => adminLogPage(filters, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    placeholderData: keepPreviousData,
  });
  const entries = log.data?.pages.flatMap((p) => p.entries) ?? [];
  const today = log.data?.pages[0]?.today;

  const personal = [...data.personal.map((p) => p.mailbox).filter((m): m is MailboxInfo => !!m), ...data.formerPersonal];
  const label = (mb: MailboxInfo) =>
    inboxLabel({ mailboxKind: mb.kind, mailboxAddress: mb.address, mailboxUserId: mb.userId }, byId);

  return (
    <div className="tile span-12">
      <div className="tile-h">
        <h3>Access log</h3>
        {today ? (
          <span className="cnt">
            Today: {formatNumber(today.opened)} opened · {formatNumber(today.checked)} checked by thread ID
          </span>
        ) : null}
      </div>
      <div className="gm-filters" role="group" aria-label="Filter the access log">
        <Icon name="filter" className="i-sm" />
        <select className="select select-sm" aria-label="Inbox" value={inbox} onChange={(e) => setInbox(e.target.value)}>
          <option value="">All inboxes</option>
          <option value="kind:personal">All personal inboxes</option>
          <option value="kind:shared">All shared inboxes</option>
          <option value="kind:crm">The CRM address</option>
          {personal.length ? (
            <optgroup label="Personal">
              {personal.map((mb) => (
                <option key={mb.id} value={`mb:${mb.id}`}>
                  {label(mb)}
                  {mb.status === 'disconnected' ? ' (disconnected)' : ''}
                </option>
              ))}
            </optgroup>
          ) : null}
          {data.shared.length ? (
            <optgroup label="Shared">
              {data.shared.map((mb) => (
                <option key={mb.id} value={`mb:${mb.id}`}>
                  {mb.address}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
        <select className="select select-sm" aria-label="Action" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">Everything</option>
          {ACCESS_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {ACTION_LABELS[a]}
            </option>
          ))}
        </select>
        {filtered ? (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => {
              setInbox('');
              setAction('');
            }}
          >
            Clear
          </button>
        ) : null}
        {log.isFetching && !log.isFetchingNextPage && log.data ? <span className="spin gm-spin" role="status" aria-label="Updating" /> : null}
      </div>
      {log.isLoading ? (
        <Skeleton rows={4} />
      ) : log.isError && !log.data ? (
        <Callout
          tone="danger"
          role="alert"
          title="The access log did not load"
          actions={
            <button type="button" className="btn btn-sm btn-tile" onClick={() => log.refetch()}>
              <Icon name="refresh" />
              Try again
            </button>
          }
        >
          {errorMessage(log.error)}
        </Callout>
      ) : entries.length === 0 ? (
        filtered ? (
          <Empty icon="filter" title="Nothing matches these filters" className="gm-empty">
            Choose another inbox or action, or clear the filters.
          </Empty>
        ) : (
          <Empty icon="eye" title="Nothing logged yet" className="gm-empty">
            Each time Clientbook checks, opens or files mail from an inbox, it shows here.
          </Empty>
        )
      ) : (
        <div className={log.isPlaceholderData ? 'gm-log stale' : 'gm-log'} tabIndex={0} aria-label="Access log entries" role="region">
          <LogRows entries={entries} byId={byId} />
        </div>
      )}
      {log.hasNextPage ? (
        <button type="button" className="btn btn-sm btn-ghost gm-more" onClick={() => log.fetchNextPage()} disabled={log.isFetchingNextPage}>
          {log.isFetchingNextPage ? 'Loading' : 'Show older'}
        </button>
      ) : null}
    </div>
  );
}
